import {Injectable, OnDestroy} from '@angular/core';
import {ViewFault} from '@docma-mm-core/fault/fault';
import {FaultPin, Plan} from '@docma-mm-core/plan/plan';
import {SelectionService} from '@mydocma/component-library/src/lib/selection';
import {firstValueFrom, of} from 'rxjs';
import {distinct, filter, map, switchMap} from 'rxjs/operators';
import {MmFaultsLoaderService} from '../../../mm-faults-loader.service';
import {MmPlansFilterService} from '../../services/mm-plans-filter.service';
import {MmPlansManagerService} from '../../services/mm-plans-manager.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {MmFaultsViewService} from '../../../mm-faults-view/services/mm-faults-view.service';
import {FaultService} from '@docma-mm-core/fault/fault.service';
import {MarkerIconTypeEnum, Pin, PinService} from '@mydocma/tile-viewer';
import {MmProjectPermissionsService} from '../../../../mm-project/mm-project-permissions-service';
import {AppContainerService} from '@mydocma/app-container';
import {BpPinLocationTargetEnum} from '../../../../bp-common/models/bp-pin';
import {MmProjectService} from '../../../../mm-project/mm-project.service';
import {AttributeService} from '@docma-mm-core/attribute/attribute.service';


@UntilDestroy()
@Injectable()
export class MmPlanPinService implements OnDestroy {

  private pinService: PinService;
  public plansWithPins$ = of<Plan[]>([]);

  constructor(
    private readonly plansFilterService: MmPlansFilterService,
    private readonly selectionFaultService: SelectionService<ViewFault>,
    private readonly plansManagerService: MmPlansManagerService,
    private readonly faultsLoaderService: MmFaultsLoaderService,
    private readonly faultsViewService: MmFaultsViewService,
    private readonly faultService: FaultService,
    public readonly permissionService: MmProjectPermissionsService,
    public appContainerService: AppContainerService,
    private projectService: MmProjectService,
    private readonly attributeService: AttributeService) {

    this.faultService.onFaultDeleted.pipe(untilDestroyed(this)).subscribe(async (changedFault: ViewFault) => {
      const allPlans = await firstValueFrom(this.plansManagerService.allPlans$);
      const pinsByFault = this.groupByFault(allPlans);

      if (pinsByFault.hasOwnProperty(changedFault.uid)) {
        const existingPin = this.pinService.pins.find(pin => pinsByFault[changedFault.uid][0].pin.fault.id === pin.id || pinsByFault[changedFault.uid][0].pin.fault.uid === pin.id);
        if (existingPin) {
          /* REMOVE PIN */
          const i = pinsByFault[changedFault.uid][0].plan.pins.findIndex(x => x.fault.uid === existingPin.data.uid);
          if (i !== -1) {
            pinsByFault[changedFault.uid][0].plan.pins.splice(i, 1);
            await this.pinService.removePin(existingPin);
          }
        }
      }
    });

    this.faultService.onFaultChanged.pipe(untilDestroyed(this)).subscribe(async (changedFault: ViewFault) => {
      const allPlans = await firstValueFrom(this.plansManagerService.allPlans$);
      const pinsByFault = this.groupByFault(allPlans);

      if (pinsByFault.hasOwnProperty(changedFault.uid)) {
        const existingPin = this.pinService.pins.find(pin => pinsByFault[changedFault.uid][0].pin.fault.id === pin.id || pinsByFault[changedFault.uid][0].pin.fault.uid === pin.id);
        if (existingPin) {
          if (changedFault.pin == null) {
            /* REMOVE PIN */
            const i = pinsByFault[changedFault.uid][0].plan.pins.findIndex(x => x.fault.uid === existingPin.data.uid);
            if (i !== -1) {
              pinsByFault[changedFault.uid][0].plan.pins.splice(i, 1);
              await this.pinService.removePin(existingPin);
            }
          }
          else {
            /* UPDATE PIN */
            const fault = pinsByFault[changedFault.uid][0].pin.fault;
            existingPin.data = fault;
            await this.setMarkerOptions(existingPin, fault);
            await this.pinService.updatePin(existingPin);
            // await this.pinService.selectPin(existingPin);
          }
        }
        else {
          console.warn('NO PINSERVICE-PIN FOUND !!!');
        }
      }
      else {
        /* UPDATE NEW PIN */
        if (changedFault.pin != null) {
          const plan = allPlans.find(p => p.id === changedFault.pin.FileId);
          const addedPin = this.pinService.getPinById(changedFault.id) ?? this.pinService.getPinById(changedFault.uid);
          if (addedPin) {
            const pinIndex = plan.pins.findIndex(x => x.fault.uid === changedFault.uid);
            if (plan && pinIndex === -1) {
              plan.pins.push({fault: changedFault, coordinate: changedFault.pin, bpPin: changedFault.bpPin} as FaultPin);
              await this.setMarkerOptions(addedPin, changedFault);
              await this.pinService.updatePin(addedPin);
              // await this.pinService.selectPin(addedPin);
            }
          }
        }
      }
    });

    this.plansWithPins$ = this.faultsLoaderService.faults.pipe(
      untilDestroyed(this),
      switchMap(faults => this.plansManagerService.allPlans$.pipe(
        switchMap(allPlans => this.plansManagerService.plansByStructure$.pipe(map(plans => ({
          faults,
          allPlans,
          plans
        })))),
      )),
      filter(({plans}) => plans?.length > 0),
      switchMap(({faults, allPlans, plans}) => {
        const updatedPlans: {[id: number]: {Plan: Plan, Pins: FaultPin[]}} = {};
        const faultsByPlan = this.groupByPlan(faults.loadedFaults);

        allPlans.forEach(plan => {
          const pins: FaultPin[] = [];

          if (!updatedPlans.hasOwnProperty(plan.id)) {
            if (faultsByPlan.hasOwnProperty(plan.id)) {
              faultsByPlan[plan.id].forEach(fault => {
                pins.push({fault, coordinate: fault.pin, bpPin: fault.bpPin});
              });
            }
            updatedPlans[plan.id] = {Plan: plan, Pins: pins};
            plan.pins = pins;
          }
        });

        plans.forEach(plan => {
          const pins: FaultPin[] = [];

          if (!updatedPlans.hasOwnProperty(plan.id)) {
            if (faultsByPlan.hasOwnProperty(plan.id)) {
              faultsByPlan[plan.id].forEach(fault => {
                pins.push({fault, coordinate: fault.pin, bpPin: fault.bpPin});
              });
            }
            updatedPlans[plan.id] = {Plan: plan, Pins: pins};
            plan.pins = pins;
          }
        });

        console.debug(plans, '### plans set ### 01');

        return of(plans);
      }),
    );

    this.selectionFaultService.selectedItems.pipe(
      distinct(),
      untilDestroyed(this),
      switchMap(selectedFaults => this.plansFilterService.filterdPlans$.pipe(map(plans => ({selectedFaults, plans})))),
      filter(({plans}) => plans?.length > 0),
    ).subscribe({
      next: ({selectedFaults, plans}) => {
        plans.forEach(plan => plan.selectedPins = selectedFaults.filter(fault => fault.pin?.FileId === plan.id).length);
      },
      error: error => console.error(error)
    });

    this.faultsViewService.currentFault.pipe(
      untilDestroyed(this),
      switchMap((currentFault) => this.plansFilterService.filterdPlans$.pipe(map(plans => ({currentFault, plans})))),
      filter(({currentFault, plans}) => plans?.length > 0 && !!currentFault),
    ).subscribe({
      next: ({currentFault, plans}) => {
        plans.forEach(plan => plan.selectedPins = (currentFault.pin?.FileId === plan?.id) ? 1 : 0);
      },
      error: (error) => console.error(error)
    });
  }

  ngOnDestroy(): void {
  }

  public registerPlanPinService(pinService: PinService) {
    this.pinService = pinService;
  }

  groupByPlan(faults: ViewFault[]) {
    const faultsByPlan: {[fileId: number]: ViewFault[]} = faults.filter(x => !!x.pin?.FileId).reduce((acc, item) => {
      const key = item.pin.FileId;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});
    return faultsByPlan;
  }

  groupByFault(plans: Plan[]) {
    const pins = plans.flatMap(plan => plan.pins.map(pin => ({plan, pin}) ) );

//        .flatMap(p => p.pins);

    const pinsByFault: {[faultId: string]: ({plan: Plan, pin: FaultPin})[]} = pins.reduce((acc: {}, item) => {
      const key = item.pin.fault.uid;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});
    return pinsByFault;
  }

  async setMarkerOptions(pin: Pin, fault: ViewFault) {
    const attribute = this.attributeService.getAttributeImage(fault.data?.Attribute?.Id);
    const project = await firstValueFrom(this.projectService.currentProject);

    pin.data.attributeImage = attribute?.Url;

    if (project?.textPinEnabled && pin.data?.pinText) {
      if (!pin.markerOptions) {
        pin.markerOptions = {iconType: MarkerIconTypeEnum.textPin};
      } else {
        pin.markerOptions.iconType = MarkerIconTypeEnum.textPin;
      }


    } else if (attribute?.Url) {
      if (!pin.markerOptions) {
        pin.markerOptions = {iconType: MarkerIconTypeEnum.attributeImage};
      } else {
        pin.markerOptions.iconType = MarkerIconTypeEnum.attributeImage;
      }
    } else {
      pin.markerOptions = {
        iconType: fault?.bpPin?.Locations.find(l => l.Type === BpPinLocationTargetEnum.bim)
          ? MarkerIconTypeEnum.defaultWithObject
          : MarkerIconTypeEnum.default
      };
    }
  }




}
